/**
 * PWWCapital — Toast Notification Context (Section 18)
 *
 * Provides a `useToast()` hook anywhere in the tree.
 *
 * API:
 *   const { toast } = useToast();
 *   toast.success('Transfer completed')
 *   toast.error('Transaction failed', { title: 'Payment Error' })
 *   toast.warning('Rate expires in 30 s')
 *   toast.info('Statement ready to download')
 *   toast.dismiss(id)
 *   toast.clear()
 *
 * Rules (Section 18 directive):
 *   - Error toasts persist until manually dismissed
 *   - Success / info / warning auto-dismiss after 5 s / 7 s
 *   - Max 5 toasts visible; oldest non-error is evicted first
 *   - ToastProvider mounts <ToastContainer> into a portal so it always floats above modals
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useReducer,
  useRef,
} from 'react';
import { ToastContainer } from '../components/Toast/Toast';

const MAX_TOASTS = 5;

/** @typedef {{ id: string; type: 'success'|'error'|'warning'|'info'; message: string; title?: string; duration?: number|null }} ToastItem */

const ToastContext = createContext(null);

//  Reducer

function toastReducer(state, action) {
  switch (action.type) {
    case 'ADD': {
      const next = [...state, action.payload];
      // Evict oldest non-error first if over limit
      if (next.length > MAX_TOASTS) {
        const evictIndex = next.findIndex((t) => t.type !== 'error');
        if (evictIndex !== -1) next.splice(evictIndex, 1);
        else next.shift(); // all errors: remove oldest
      }
      return next;
    }
    case 'REMOVE':
      return state.filter((t) => t.id !== action.id);
    case 'CLEAR':
      return [];
    default:
      return state;
  }
}

//  Provider

export function ToastProvider({ children }) {
  const [toasts, dispatch] = useReducer(toastReducer, []);
  const counter = useRef(0);

  const add = useCallback((type, message, opts = {}) => {
    const id = `toast-${Date.now()}-${++counter.current}`;
    dispatch({
      type: 'ADD',
      payload: { id, type, message, ...opts },
    });
    return id;
  }, []);

  const dismiss = useCallback((id) => {
    dispatch({ type: 'REMOVE', id });
  }, []);

  const clear = useCallback(() => {
    dispatch({ type: 'CLEAR' });
  }, []);

  const toast = {
    success: (message, opts) => add('success', message, opts),
    error:   (message, opts) => add('error',   message, opts),
    warning: (message, opts) => add('warning', message, opts),
    info:    (message, opts) => add('info',    message, opts),
    dismiss,
    clear,
  };

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

//  Hook

/**
 * @returns {{ toast: { success(msg: string, opts?: object): string; error(msg: string, opts?: object): string; warning(msg: string, opts?: object): string; info(msg: string, opts?: object): string; dismiss(id: string): void; clear(): void } }}
 */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}

export default ToastContext;
